import { isNil } from 'ramda';
import videojs, { VideoJsPlayer } from 'video.js';

import { Event, LabelKey } from '@src/types/event';
import { getIsBrowser, getIsTouchDevice } from '@src/utils/device';

import BaseNormalizer from './BaseNormalizer';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

export const PLUGIN_NAME = 'eventManagerPlayStateNormalizer';

type State = {
  seekStartTime: null | number;
  isInAdBreak: boolean;
  isFirstStart: boolean;
  isAllowNextPauseToTrigger: boolean;
  isAllowNextPauseToTriggerBeforeSeek: boolean;
  isAllowNextStartOrResumeToTrigger: boolean;
  isVisibilityChangeToTriggerPlay: boolean;
  isSeeking: boolean;
  isSeeked: boolean;
  lastResetImaCount: number;
  lastVideoContentResumeCount: number;
  isContentResumeRequested: boolean;
  isAdErrorEmitted: boolean;
  isPausedOnMouseDown: boolean;
};

interface PlayStateNormalizer {
  setState: (state: Partial<State>) => void;
  state: State;
}

class PlayStateNormalizer extends BaseNormalizer {
  static defaultState: State;
  vjsPlayer: VideoJsPlayer;
  isTouchDevice: boolean;
  thresholdToEmitStartEventIfNoContentPauseRequested = 2;

  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);
    this.vjsPlayer = player;

    this.isTouchDevice = getIsTouchDevice();

    this.setState({
      lastVideoContentResumeCount:
        player.contextPlugin.state.videoContentResumeCount || 0,
    });

    this.setupContribAdsListeners();
    this.setupPlayStateListeners();
    this.setupSeekListeners();
    this.setupVisibilityListeners();
    this.setupResetStateListeners();
  }

  setupContribAdsListeners() {
    this.vjsPlayer.on(Event.ADSTART, this.handleAdStart);
    this.vjsPlayer.on(Event.ADSERROR, this.handleContribAdsError);
    this.vjsPlayer.on([Event.ADEND, Event.ADSKIP], this.handleAdEndOrSkip);
  }

  disposeContribAdsListeners() {
    this.vjsPlayer.off(Event.ADSTART, this.handleAdStart);
    this.vjsPlayer.off(Event.ADSERROR, this.handleContribAdsError);
    this.vjsPlayer.on([Event.ADEND, Event.ADSKIP], this.handleAdEndOrSkip);
  }

  handleAdStart = (/* evt: videojs.EventTarget.Event */) => {
    this.setState({ isInAdBreak: true });
  };

  handleAdEndOrSkip = (/* evt: videojs.EventTarget.Event */) => {
    this.setState({ isInAdBreak: false });

    this.setState({
      isAllowNextStartOrResumeToTrigger: false,
    });
  };

  setupPlayStateListeners() {
    this.vjsPlayer.on(
      Event.CONTENT_PAUSE_REQUESTED,
      this.handleContentPauseRequested
    );
    this.vjsPlayer.on(
      Event.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );
    this.vjsPlayer.on(Event.CLICKTOPLAY, this.handleClickToPlay);
    this.vjsPlayer.on(Event.CLICKTOPAUSE, this.handleClickToPause);
    this.vjsPlayer.on(
      // we can still fire `start` event:
      // when Event.CONTENT_RESUME_REQUESTED is emitted too late, by listening to Event.CONTENT_RESUME_REQUESTED
      // when no Event.CONTENT_RESUME_REQUESTED is emitted and no ad error thrown, by listening to Event.TIMEUPDATE
      [Event.PLAYING, Event.CONTENT_RESUME_REQUESTED, Event.TIMEUPDATE],
      this.handleStartOrResume
    );
    this.vjsPlayer.on(Event.PAUSE, this.handlePause);
  }

  disposePlayStateListeners() {
    this.vjsPlayer.off(Event.PAUSE, this.handlePause);
    this.vjsPlayer.off(
      [Event.PLAYING, Event.CONTENT_RESUME_REQUESTED, Event.TIMEUPDATE],
      this.handleStartOrResume
    );
    this.vjsPlayer.off(Event.CLICKTOPLAY, this.handleClickToPlay);
    this.vjsPlayer.off(Event.CLICKTOPAUSE, this.handleClickToPause);
    this.vjsPlayer.off(
      Event.CONTENT_PAUSE_REQUESTED,
      this.handleContentPauseRequested
    );
    this.vjsPlayer.off(
      Event.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );
  }

  setupSeekListeners() {
    this.vjsPlayer.on(Event.SEEKMOUSEDOWN, this.handleSeekMouseDown);
    this.vjsPlayer.one(Event.ENDED, this.handleEnded);
  }

  disposeSeekListeners() {
    this.vjsPlayer.off(Event.SEEKMOUSEDOWN, this.handleSeekMouseDown);
    this.vjsPlayer.off(Event.SEEKMOUSEUP, this.handleSeekMouseUp);
    this.vjsPlayer.off(Event.ENDED, this.handleEnded);
  }

  setupVisibilityListeners() {
    if (!getIsBrowser()) return;

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (isNil(document.hidden)) return;

    this.setState({ isVisibilityChangeToTriggerPlay: !document.hidden });
  };

  disposeVisibilityListeners() {
    if (!getIsBrowser()) return;

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
  }

  private setupResetStateListeners() {
    this.vjsPlayer.on(
      [Event.SOURCESET, Event.CONTENTCHANGED, Event.ENDED],
      this.handleResetState
    );
  }

  private disposeResetStateListeners() {
    this.vjsPlayer.off(
      [Event.SOURCESET, Event.CONTENTCHANGED, Event.ENDED],
      this.handleResetState
    );
  }

  private getContribAds() {
    return this.vjsPlayer.ads;
  }

  private getContribAdsState() {
    const ads = this.getContribAds();

    return {
      isInAdMode: ads?.isInAdMode?.(),
      inAdBreak: ads?.inAdBreak?.(),
      isWaitingForAdBreak: ads?.isWaitingForAdBreak?.(),
      isContentResuming: ads?.isContentResuming?.(),
    };
  }

  getContextState() {
    return this.vjsPlayer.contextPlugin.state;
  }

  handleStartOrResume = (evt: videojs.EventTarget.Event) => {
    if (this.getPlayerError()) {
      return;
    }

    if (
      evt.type === Event.TIMEUPDATE &&
      (!this.state.isFirstStart ||
        this.state.isInAdBreak ||
        (this.getCurrentTime() || 0) -
          this.vjsPlayer.contextPlugin.state.initialStartTime <
          this.thresholdToEmitStartEventIfNoContentPauseRequested)
    ) {
      return;
    }

    // re-register handleEnded again which may be deregister in handleEnded
    this.vjsPlayer.off(Event.ENDED, this.handleEnded);
    this.vjsPlayer.one(Event.ENDED, this.handleEnded);

    const { blockAd, isGlobalGoogleImaReady, isControllerTriggeredPlay } =
      this.getContextState();
    const isImaSdkValid = !!(
      isGlobalGoogleImaReady && this.vjsPlayer.adsManagerPlugin.getImaInstance()
    );

    const {
      isContentResumeRequested,
      isAdErrorEmitted,
      isInAdBreak,
      isFirstStart,
      isAllowNextStartOrResumeToTrigger,
      isVisibilityChangeToTriggerPlay,
      isSeeking,
      isSeeked,
    } = this.state;

    let eventName = null;

    if (evt.type === Event.TIMEUPDATE) {
      eventName = Event.START;
    } else if (evt.type === Event.PLAYING) {
      if (
        !isImaSdkValid ||
        isAdErrorEmitted ||
        isControllerTriggeredPlay ||
        ((isContentResumeRequested || (blockAd && !isSeeking)) &&
          isFirstStart) || // if isContentResumeRequested is set too late, will be handle in the else cause below
        isAllowNextStartOrResumeToTrigger ||
        (isVisibilityChangeToTriggerPlay && !isInAdBreak && !isFirstStart) ||
        (this.getIsIosFullscreen() && !isInAdBreak)
      ) {
        eventName = isFirstStart ? Event.START : Event.RESUME;
      }
    } else if (evt.type === Event.CONTENT_RESUME_REQUESTED) {
      // if Event.CONTENT_RESUME_REQUESTED is emitted too late after Event.PLAYING for the first play
      // we emit the Event.Start explicitly
      if (isFirstStart) {
        eventName = Event.START;
      }
    }

    if (isSeeked || isVisibilityChangeToTriggerPlay) {
      this.setState({
        ...(isSeeked && { isSeeked: false }),
        ...(isVisibilityChangeToTriggerPlay && {
          isVisibilityChangeToTriggerPlay: false,
        }),
      });
    }

    if (eventName) {
      if (isControllerTriggeredPlay) {
        this.player.contextPlugin.setState({
          isControllerTriggeredPlay: false,
        });
      }

      this.setState({
        lastResetImaCount: this.getContextState().resetImaCount,
        isContentResumeRequested: false,
        isAdErrorEmitted: false,
        // for blockAd, isAllowNextPauseToTrigger is set in handleClickToPause, or set in fullscreen mode in handlePause
        ...(!blockAd && { isAllowNextPauseToTrigger: true }),
        isAllowNextStartOrResumeToTrigger: false, // TODO not check any side effect yet
        isFirstStart: false,
      });

      const autoplayConfig = this.getAutoplay();

      this.trigger(eventName, {
        ...this.createCommonEventData(),
        ...(eventName === Event.START && {
          [LabelKey.IS_AUTO_PLAY]: !!(
            typeof autoplayConfig === 'string' || autoplayConfig === true
          ),
          [LabelKey.IS_MUTED]: this.getMuted(),
          [LabelKey.VIDEO_LENGTH]: this.getDuration(),
        }),
      });
    } else {
      if (isSeeked && evt.type === Event.PLAYING) {
        // setTimeout because always want the setState below is later than calling of handlePause after seek, especially on android
        setTimeout(() => {
          this.setState({
            isAllowNextPauseToTrigger:
              this.state.isAllowNextPauseToTriggerBeforeSeek,
          });
        }, 100);
      }
    }
  };

  handlePause = () => {
    if (this.getPlayerError()) {
      return;
    }

    const { isControllerTriggeredPause } = this.getContextState();

    // pause on ios native fullscreen wont trigger handleClickPause, need this workaround to fire pause event
    if (this.getIsIosFullscreen() && this.getContextState().blockAd) {
      this.setState({
        isAllowNextPauseToTrigger: true,
      });
    }

    if (
      (this.state.isAllowNextPauseToTrigger || isControllerTriggeredPause) &&
      !this.state.isInAdBreak
    ) {
      if (isControllerTriggeredPause) {
        this.player.contextPlugin.setState({
          isControllerTriggeredPause: false,
        });
      }
      this.setState({
        isAllowNextPauseToTrigger: false,
      });
      this.trigger(Event.PAUSE, this.createCommonEventData());
    }
  };

  handleSeekMouseDown = (
    evt: videojs.EventTarget.Event,
    data?: { originalType?: 'touchstart' | 'mousedown' }
  ) => {
    // skip mouseup on touch device, otherwise this callback will be called twice
    if (this.isTouchDevice && data?.originalType === 'mousedown') {
      return;
    }

    // deregister previous listener in case if called yet to prevent double trigger
    this.vjsPlayer.off(Event.SEEKMOUSEUP, this.handleSeekMouseUp);
    this.vjsPlayer.one(Event.SEEKMOUSEUP, this.handleSeekMouseUp);

    this.setState({
      isSeeking: true,
      isAllowNextPauseToTriggerBeforeSeek: this.state.isAllowNextPauseToTrigger,
      isAllowNextPauseToTrigger: false,
      isPausedOnMouseDown: this.player.paused(),
      seekStartTime: this.getCurrentTime(),
      isAllowNextStartOrResumeToTrigger: false,
    });
  };

  handleSeekMouseUp = (
    evt: videojs.EventTarget.Event,
    data?: { originalType?: 'touchend' | 'mouseup' }
  ) => {
    // skip mouseup on touch device, otherwise this callback will be called twice
    if (this.isTouchDevice && data?.originalType === 'mouseup') {
      return;
    }

    const { seekStartTime, isPausedOnMouseDown } = this.state;
    this.setState({
      isPausedOnMouseDown: false,
      isSeeking: false,
      isSeeked: true,
      seekStartTime: null,
    });

    this.trigger(Event.SEEK, {
      ...this.createCommonEventData(),
      [LabelKey.IS_PAUSED]: isPausedOnMouseDown,
      [LabelKey.VIDEO_FROM_TIME]: seekStartTime,
      [LabelKey.VIDEO_TO_TIME]: this.getCurrentTime(),
    });
  };

  private handleContentPauseRequested = () => {
    this.setState({ isContentResumeRequested: false });
  };

  private handleContentResumeRequested = () => {
    this.setState({ isContentResumeRequested: true });
  };

  handleEnded = () => {
    const { blockAd } = this.getContextState();
    const isPaused = this.vjsPlayer.paused();

    if (blockAd && isPaused) {
      this.setState({ isAllowNextPauseToTrigger: true });
      this.handlePause();
    }

    // remove handleEnded to prevent double fire Event.ENDED on some device, e.g. android
    // will re-register in handleStartOrResume
    this.vjsPlayer.off(Event.ENDED, this.handleEnded);
    this.setState({ seekStartTime: null });
  };

  private handleClickToPlay = () => {
    const { resetImaCount } = this.getContextState();

    const isPreviouslyResetIma = this.state.lastResetImaCount !== resetImaCount;

    const { isFirstStart } = this.state;

    // need to check !isFirstStart for click-to-play video with ads
    if (!isPreviouslyResetIma && !isFirstStart) {
      this.setState({ isAllowNextStartOrResumeToTrigger: true });
    }
  };

  private handleClickToPause = () => {
    this.setState({
      isAllowNextStartOrResumeToTrigger: true,
      isAllowNextPauseToTrigger: true,
    });
  };

  private handleResetState = (evt: videojs.EventTarget.Event) => {
    const { isFirstStart, isAllowNextStartOrResumeToTrigger, ...stateToReset } =
      PlayStateNormalizer.defaultState;

    this.setState({
      ...stateToReset,
      // TODO change to isAllowNextStartOrResumeToTrigger as replay already handled by click-to-play emit
      // update: may be can still keep
      ...(evt.type === Event.ENDED
        ? { isAllowNextStartOrResumeToTrigger: true }
        : { isAllowNextStartOrResumeToTrigger }),
    });
  };

  private handleContribAdsError = (evt: videojs.EventTarget.Event) => {
    this.vjsPlayer.trigger(Event.X_AD_ERROR_EVENT, {
      adErrorEvent: evt?.data?.AdErrorEvent,
    });

    this.setState({ isAdErrorEmitted: true });

    this.player.contextPlugin.addAdErrorEvent(evt);
  };

  resetState() {
    this.setState({
      ...PlayStateNormalizer.defaultState,
      // since resetImaCount in context plugin wont be reset, should reset as the value in context plugin
      lastResetImaCount:
        this.player.contextPlugin?.state?.resetImaCount ??
        PlayStateNormalizer.defaultState.lastResetImaCount,
    });
  }

  dispose() {
    this.disposeResetStateListeners();
    this.disposeVisibilityListeners();
    this.disposeSeekListeners();
    this.disposePlayStateListeners();
    this.disposeContribAdsListeners();
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, PlayStateNormalizer);

export type PlayStateNormalizerFactoryFn = (
  options?: Options
) => PlayStateNormalizer;

PlayStateNormalizer.defaultState = {
  lastResetImaCount: 0,
  lastVideoContentResumeCount: 0,
  seekStartTime: null,
  isInAdBreak: false,
  isFirstStart: true,
  isAllowNextPauseToTrigger: false,
  isAllowNextPauseToTriggerBeforeSeek: false,
  isAllowNextStartOrResumeToTrigger: false,
  isVisibilityChangeToTriggerPlay: false,
  isSeeking: false,
  isSeeked: false,
  isContentResumeRequested: false,
  isAdErrorEmitted: false,
  isPausedOnMouseDown: false,
};

export default PlayStateNormalizer;
